<template>
  <div id="console" ref="console" :class="{ show: showConsole }" @click="toggleDebugMode">
    <div v-show="showConsole" class="controls flex flex-col">
      <div class="mx-1 py-2 flex flex-row text-left">
        <div class="mx-1 py-2">web: v{{ version }}</div>
        <div class="mx-1 py-2">app: {{ platform }} v{{ appVersion }}</div>
        <button type="button" class="hide" @click.stop="showConsole = false">hide</button>
      </div>
      <div class="mx-1 py-2 flex flex-row text-right">
        <div class="my-1 flex items-center">
          <span class="mx-2">UI</span>
          <select name="ui-env" id="ui-env" v-model="uiEnvIndex" class="p-1">
            <option
              v-for="(env, index) in envs.ui"
              :key="index"
              :value="index"
              :selected="index === uiEnvIndex"
              >{{ env.name }}</option
            >
          </select>
        </div>
        <div class="my-1 flex items-center">
          <span class="mx-2">API</span>
          <select name="api-env" id="api-env" v-model="apiEnvIndex" class="p-1">
            <option
              v-for="(env, index) in envs.api"
              :key="index"
              :value="index"
              :selected="index === apiEnvIndex"
              >{{ env.name }}</option
            >
          </select>
        </div>
        <div class="my-1 flex items-center">
          <span class="mx-2">Mock</span>
          <select name="mock-env" id="mock-env" v-model="mockScan" class="p-1">
            <option :value="0" :selected="!mockScan">disabled</option>
            <option :value="1" :selected="mockScan === 1">Gen2 (2-step)</option>
            <option :value="2" :selected="mockScan === 2">Gen2 (1-step)</option>
            <option :value="3" :selected="mockScan === 3">Gen1 (2-step)</option>
            <option :value="4" :selected="mockScan === 4">Gen1 (1-step)</option>
          </select>
        </div>
      </div>
    </div>
    <div ref="logs" class="logs"></div>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import { debounce } from 'lodash';
import { SET_DEBUG_MODE } from '@/store/modules/app';
import { EVENTS as API_EVENTS } from '@/events/api';
import { EVENTS as HW_EVENTS } from '@/events/hardware';
import ENVS, { INITIAL_INDEX } from '@/modules/envs';
import ApiActions from '@/modules/api-actions';
import ScanProcess from '@/modules/scan-process';
import * as Sentry from '@sentry/vue';

const IGNORE_MESSAGE = [
  'No such user',
  'password is invalid',
  'Account already exists',
  'measurement failed, errors: 900',
  'measurement I pose failed, errors: 900',
  'timeout waiting for measurements to load',
];

export default {
  name: 'Console',
  created() {
    this.debouncedSendFeedback = debounce(this.sendFeedback, 5000);
  },
  mounted() {
    this.addDebugConsole();
  },
  data: () => ({
    showConsole: false,
    toggleDebugModeCounter: 0,
    uiEnvIndex: INITIAL_INDEX,
    apiEnvIndex: INITIAL_INDEX,
    mockScan: ENVS.mock,
  }),
  methods: {
    addDebugConsole() {
      const original = console.log;
      const $console = this.$refs.logs;
      console.log = (...args) => {
        original(...args);
        if (!$console) return;

        const apiKey = this.envs.api[this.apiEnvIndex].key;
        const timestamp = new Date().toLocaleString('zh', { hour12: false });
        const msg = args
          .map(arg => (typeof arg === 'string' ? arg : JSON.stringify(arg)))
          .join(' ')
          .replace(apiKey, `${apiKey.substring(0, 3)}***`);
        $console.insertAdjacentHTML('afterbegin', `<p>${timestamp}: ${msg}</p>`);
        if (typeof args[0] === 'string' && args[0].startsWith('[ERROR]')) {
          if (msg.includes('[USER_REPORT]')) {
            this.sendFeedback();
            return;
          }

          if (localStorage) {
            localStorage.setItem('tg3d-last-error', msg);
          }
          Sentry.captureMessage(msg, {
            level: 'error',
            tags: {
              source: 'console',
              tid: ScanProcess.tid,
              scanner_id: ScanProcess.scannerId,
              provider: ScanProcess.provider,
            },
          });
          const isIgnore = IGNORE_MESSAGE.some(m => msg.includes(m));
          if (!isIgnore) {
            this.debouncedSendFeedback();
          }
        }
      };
    },
    toggleDebugMode() {
      if (this.debugMode) {
        this.showConsole = true;
        return;
      }

      this.toggleDebugModeCounter += 1;
      if (this.toggleDebugModeCounter > 8) {
        this.$store.commit(`app/${SET_DEBUG_MODE}`, !this.debugMode);
        this.toggleDebugModeCounter = 0;
      }
    },
    debouncedSendFeedback() {}, // will be created on instance creation
    sendFeedback() {
      const $console = this.$refs.logs;
      if (!$console) return;

      const scannerUuid = localStorage ? localStorage.getItem('tg3d-last-scanner') || '' : '';
      const scanTid = localStorage ? localStorage.getItem('tg3d-last-tid') || '' : '';
      const lastError = localStorage ? localStorage.getItem('tg3d-last-error') || '' : '';
      const textContent = [...$console.querySelectorAll('p')].map(p => p.textContent);
      const comments = `
<p>PLATFORM: ${this.platformInfo}</p>
<p>APP VERSION: ${this.appVersion}</p>
<p>DEVICE: ${this.deviceInfo}</p>
<p>MODE: ${this.mode}</p>
<p>MTM_USER: ${JSON.stringify(this.me)}</p>
<p>SCANNER_ID: ${this.fixedScannerId}</p>
<p>LAST SCANNER_ID: ${scannerUuid}</p>
<p>LAST SCAN_TID: ${scanTid}</p>
<p>LAST ERROR: ${lastError}</p>
<p>LAST 10: <br>
${textContent.slice(0, 10).join('<br>')}
</p>
`;
      import('jszip').then(({ default: JSZip }) => {
        const zip = new JSZip();
        zip.file('log.txt', textContent.join('\n'));
        zip
          .generateAsync({ type: 'blob' })
          .then(file => {
            const data = new FormData();
            const logTitle = `Cloudzet V2 ${this.mode ? `[${this.mode}]` : ''}(${this.version})`;
            data.append('app', logTitle);
            data.append('comments', comments);
            data.append('logs', file, 'logs.zip');
            return this.$api.mtm.post('/api/v1/feedbacks', data, {
              headers: { 'Content-Type': 'multipart/form-data' },
            });
          })
          .then(() => {})
          .catch(() => {});
      });
    },
  },
  computed: {
    envs() {
      return ENVS;
    },
    ...mapState('app', ['debugMode', 'mode', 'version']),
    ...mapState('hardware', ['platform', 'appVersion']),
    ...mapState('mtmUser', ['me', 'fixedScannerId']),
    ...mapGetters('hardware', ['platformInfo', 'deviceInfo']),
    currentUIEnv() {
      return this.envs.ui[this.uiEnvIndex];
    },
    currentAPIEnv() {
      return this.envs.api[this.apiEnvIndex];
    },
  },
  watch: {
    currentAPIEnv(nv) {
      this.$events.emit(API_EVENTS.SET_API_HOST, nv.host);
      this.$events.emit(API_EVENTS.SET_API_KEY, nv.key);
      console.log('currentAPIEnv changed: ', nv.host);
      ApiActions.user.logout();
      this.$events.goto('Login');
    },
    currentUIEnv(nv) {
      this.$events.emit(HW_EVENTS.WEB_URL, nv.host);
      console.log('currentUIEnv changed: ', nv.host);
    },
    mockScan(nv) {
      ENVS.mock = nv;
    },
  },
};
</script>

<style lang="scss" scoped>
#console {
  position: fixed;
  bottom: 0;
  left: 0;
  width: 10%;
  height: 100px;
  background: black;
  opacity: 0;
  display: flex;
  flex-direction: column;
  z-index: 100;
  &.show {
    opacity: 1;
    width: 100%;
    height: 50vh;
  }
  .controls {
    color: blue;
    button {
      @apply mx-2 px-2;
      border: 2px solid white;
      text-transform: uppercase;
    }
    .hide {
      @apply ml-auto;
      min-width: 40px;
    }
    .current {
      color: red;
    }
  }
  .logs {
    display: flex;
    flex: 1;
    flex-direction: column;
    color: red;
    overflow: scroll;
  }
}
</style>
